import React, { useCallback, useMemo } from "react";
import { get } from "lodash";
import makeStyles from "@mui/styles/makeStyles";
import { Theme } from "@mui/material/styles";
import { BaseRow, CellProps, RowProps, RowType } from "@shared/components/DataGrid/models";
import { Cell } from "@shared/components/DataGrid/components";

const fontWeightMap = {
  [RowType.Regular]: "fontWeightMedium",
  [RowType.SubHeader]: "fontWeightBold",
  [RowType.BoldHeader]: "fontWeightBold",
  [RowType.Highlighted]: "fontWeightMedium",
  [RowType.Divider]: "fontWeightRegular",
  [RowType.Video]: "fontWeightMedium",
  [RowType.HighlightedReadonly]: "fontWeightMedium",
  [RowType.Readonly]: "fontWeightMedium",
};
const bgColorsMap = {
  [RowType.Regular]: "grey.100",
  // Folder (SubHeader) rows use a light grey background as before.
  [RowType.SubHeader]: "grey.300",
  [RowType.BoldHeader]: "grey.300",
  [RowType.Highlighted]: "accent.light",
  [RowType.Divider]: "common.white",
  [RowType.Video]: "grey.100",
  [RowType.HighlightedReadonly]: "accent.light",
  [RowType.Readonly]: "grey.100",
};
const defaultMeta = {
  type: RowType.Regular,
};

const useStyles = makeStyles<
  Theme,
  {
    type: RowType;
    alternateRowColor: boolean | undefined;
    rowHorizontalMargin?: number;
    solidGreyBackground?: boolean;
    useVerticalGap?: boolean;
    highlightBorder?: boolean;
    highlightBorderColor?: string;
    dimmed?: boolean;
  }
>((theme) => ({
  rowContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    width: "100%",
    position: "relative",
    // spacing(1) == 8px: only rows with useVerticalGap get a vertical gap
    // ABOVE the row (used for nested folder headers in the Dues view).
    paddingTop: ({ useVerticalGap }) => (useVerticalGap ? theme.spacing(2) : 0),
    boxSizing: "border-box",
    backgroundColor: theme.palette.common.white,
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      boxShadow: ({ highlightBorder, highlightBorderColor }) =>
        highlightBorder ? `inset 0 0 0 2px ${highlightBorderColor || theme.palette.info.main}` : "none",
      pointerEvents: "none",
      zIndex: 2,
    },
  },
  row: {
    display: "flex",
    alignItems: "center",
    flex: "1 1 auto",
    width: "100%",
    margin: ({ alternateRowColor, rowHorizontalMargin }) =>
      alternateRowColor ? theme.spacing(0, 0) : theme.spacing(0, rowHorizontalMargin ?? 2),
    padding: ({ type }) => (type === RowType.Divider ? theme.spacing(0, 2.5) : "initial"),
    fontStyle: ({ type }) => (type === RowType.Divider ? "italic" : "inherit"),
    fontSize: ({ type }) => (type === RowType.SubHeader || type === RowType.BoldHeader ? 15 : 14),
    fontWeight: ({ type }) => get(theme.typography, fontWeightMap[type]),
    filter: ({ dimmed }) => (dimmed ? "blur(2px)" : "none"),
    opacity: ({ dimmed }) => (dimmed ? 0.4 : 1),
    pointerEvents: ({ dimmed }) => (dimmed ? "none" : "auto"),
    background: ({ alternateRowColor, type, solidGreyBackground }) => {
      // When alternateRowColor is enabled, row backgrounds are handled via rowColor classes.
      if (alternateRowColor) return "";

      // Always respect highlighted states (pending documents) so they keep the light yellow
      // background even when rendered under nested folders.
      if (type === RowType.Highlighted || type === RowType.HighlightedReadonly) {
        return get(theme.palette, bgColorsMap[type]);
      }

      // Otherwise, allow certain rows (e.g. DataRooms nested folder documents) to force a solid
      // light-grey block without changing global behaviour for other grids.
      if (solidGreyBackground) {
        return theme.palette.grey["100"];
      }

      return get(theme.palette, bgColorsMap[type]);
    },
    "&:hover": {
      background: ({ type }) =>
        type !== RowType.SubHeader &&
        type !== RowType.Divider &&
        (type === RowType.Highlighted || type === RowType.HighlightedReadonly)
          ? theme.palette.accent.main
          : theme.palette.grey["300"],
    },
  },
  evenRow: { backgroundColor: "#EBEBEB" },
  oddRow: { backgroundColor: theme.palette.grey[50] }, //{ backgroundColor: theme.palette.common.white },
  rowDivider: { borderTop: "1px solid rgba(9, 53, 73, 0.6)", backgroundColor: theme.palette.common.white },
  boldHeader: { borderTop: "3px solid rgba(9, 53, 73, 0.7)", backgroundColor: theme.palette.common.white },
  regulorRow: { backgroundColor: "#EBEBEB" },
}));

const Row = <D extends BaseRow>(props: RowProps<D>) => {
  const { style, ...rest } = props as RowProps<D>;

  const meta = useMemo(() => ({ ...defaultMeta, ...(rest.original.rowMeta || {}) }), [rest.original.rowMeta]);
  const classes = useStyles({
    type: meta.type,
    alternateRowColor: rest.alternateRowColor,
    rowHorizontalMargin: rest.rowHorizontalMargin,
    solidGreyBackground: meta.solidGreyBackground,
    useVerticalGap: meta.useVerticalGap,
    highlightBorder: meta.highlightBorder,
    highlightBorderColor: meta.highlightBorderColor,
    dimmed: meta.dimmed,
  });

  const renderCell = useCallback((c: CellProps<D>) => {
    const { key } = c.getCellProps();

    return <Cell {...c} key={key} />;
  }, []);

  const getClassRow = () => {
    let rowClass = "";

    if (rest.alternateRowColor) {
      if (rest.rowColor === "light") {
        rowClass = `${classes.oddRow}`;
      } else if (rest.rowColor === "grey") {
        rowClass = `${classes.evenRow}`;
      } else if (rest.rowColor === "bold") {
        rowClass = `${classes.boldHeader}`;
      } else if (rest.rowColor === "divider") {
        rowClass = `${classes.rowDivider}`;
      }
    }

    return rowClass;
  };

  const alterClass = getClassRow();

  return (
    <div
      {...rest.getRowProps({
        style,
        ...(meta.highlightId ? { "data-highlight-id": meta.highlightId } : {}),
      })}
    >
      <div className={classes.rowContainer}>
        <div data-testid="datagrid-row" className={`${classes.row} ${alterClass} `}>
          {meta.type === RowType.Divider ? rest.cells[0].value : rest.cells.map(renderCell)}
        </div>
      </div>
    </div>
  );
};

export default Row;
